import { formatAccountPoints } from '../points/format';

export type LeaderboardSelection = {
  period: 'week' | 'all';
  week?: string;
  page: number;
};

export function readLeaderboardSelection(search: string): LeaderboardSelection {
  const params = new URLSearchParams(search);
  const candidate = Number(params.get('leaderPage'));
  return {
    period: params.get('period') === 'all' ? 'all' : 'week',
    week: params.get('week') || undefined,
    page: Number.isSafeInteger(candidate) && candidate > 0 ? candidate : 1,
  };
}

export function leaderboardUrl(href: string, selection: LeaderboardSelection) {
  const url = new URL(href);
  url.searchParams.set('period', selection.period);
  if (selection.week) url.searchParams.set('week', selection.week);
  else url.searchParams.delete('week');
  if (selection.page > 1)
    url.searchParams.set('leaderPage', String(selection.page));
  else url.searchParams.delete('leaderPage');
  return url;
}

export function rankLabel(rank: number) {
  if (rank <= 1000) return rank.toLocaleString('en-US');
  let ceiling = 10000;
  while (rank > ceiling) ceiling *= 10;
  const scale = ceiling >= 1e9 ? 1e9 : ceiling >= 1e6 ? 1e6 : 1e3;
  return `Top ${ceiling / scale}${scale === 1e9 ? 'B' : scale === 1e6 ? 'M' : 'K'}`;
}

/** Exact desktop amounts; abbreviated mobile values never pass through a float. */
export function pointsLabel(value: string, compact = false) {
  const unsigned = value.startsWith('-') ? '0' : value;
  if (!compact) return formatAccountPoints(unsigned);
  const [whole, fraction = ''] = unsigned.split('.');
  const cents = BigInt(whole) * 100n + BigInt((fraction + '00').slice(0, 2));
  const scales = [
    [100000000000n, 'B'],
    [100000000n, 'M'],
    [100000n, 'K'],
  ] as const;
  for (const [scale, suffix] of scales) {
    if (cents < scale) continue;
    const hundredths = (cents * 100n) / scale;
    const decimals = String(hundredths % 100n)
      .padStart(2, '0')
      .replace(/0+$/, '');
    return `${hundredths / 100n}${decimals ? '.' + decimals : ''}${suffix}`;
  }
  return formatAccountPoints(unsigned);
}

/** First, current neighbourhood and actual final page, with no duplicate buttons. */
export function pageItems(
  page: number,
  lastPage: number,
): (number | 'ellipsis')[] {
  const last = Math.max(1, lastPage);
  const current = Math.max(1, Math.min(page, last));
  const candidates = new Set([1, last, current - 1, current, current + 1]);
  if (current <= 2) candidates.add(3);
  if (current >= last - 1) candidates.add(last - 2);
  const pages = [...candidates]
    .filter((n) => n > 0 && n <= last)
    .sort((a, b) => a - b);
  const output: (number | 'ellipsis')[] = [];
  for (const value of pages) {
    const previous = output.at(-1);
    if (typeof previous === 'number' && value - previous > 1) {
      if (value - previous === 2) output.push(previous + 1);
      else output.push('ellipsis');
    }
    output.push(value);
  }
  return output;
}

export function periodDateRange(start: number, end: number, timeZone: string) {
  const format = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    timeZone,
  });
  return `${format.format(start).replace('/', '.')}–${format.format(end - 1).replace('/', '.')}`;
}
